import { inject } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

import { ApiService } from '../services/api.service';
import { PreorderRequest } from '../models/cart.model';
import { PrepurchaseSessionStatus } from '../models/prepurchase.model';
import {
  CheckoutIntentResult,
  CheckoutReserveResult,
  CheckoutSessionState,
  PaymentIntentResult,
  PaymentProvider,
  PaymentResult,
  PaymentStatus,
  PrepurchaseIntentResult,
  PrepurchaseSessionPaymentState,
} from './payment-provider.interface';

/** Respuesta de `POST /preorders/{token}/payment-intent` (sin clientSecret). */
interface CreateIntentResponse {
  providerIntentId: string;
  status: PaymentStatus;
  amountColones: number;
}

/** Respuesta de `GET /preorders/{token}/payment-intent/{intentId}`. */
interface StatusResponse {
  status: PaymentStatus;
  transactionId?: string;
  errorCode?: string | null;
  message?: string | null;
}

/** Respuesta de `POST /checkout/{cartId}/payment-intent` (flujo diferido). */
interface CreateCheckoutIntentResponse {
  sessionToken: string;
  providerIntentId: string;
  status: PaymentStatus;
  amountColones: number;
  reservationExpiresAt?: string | null;
  reservationExpiresInSeconds?: number;
}

/** Respuesta de `POST /checkout/{cartId}/reserve` (puerta de reserva del checkout manual). */
interface CheckoutReserveResponse {
  sessionToken: string;
  reservationExpiresAt?: string | null;
  reservationExpiresInSeconds?: number;
  amountColones: number;
}

/** Respuesta de `GET /checkout/{token}` (bootstrap de la pantalla de pago). */
interface CheckoutSessionResponse {
  sessionToken: string;
  consumed: boolean;
  preorderToken?: string | null;
  providerIntentId?: string | null;
  status?: PaymentStatus;
  amountColones: number;
  reservationExpiresAt?: string | null;
  reservationExpiresInSeconds?: number;
}

/** Respuesta de `POST /checkout/{token}/complete`. */
interface CompleteCheckoutResponse {
  token: string;
}

/** Respuesta de `POST /prepurchase-sessions/{token}/payment-intent`. */
interface CreatePrepurchaseIntentResponse {
  sessionToken: string;
  providerIntentId: string;
  status: PaymentStatus;
  amountColones: number;
}

/** Respuesta de `GET /prepurchase-sessions/{token}` (rehidratación de la página de pago). */
interface PrepurchaseSessionResponse {
  session_token: string;
  status: PrepurchaseSessionStatus;
  amount_colones: number;
  currency: string;
  expires_at?: string | null;
  expires_in_seconds: number;
  provider_intent_id?: string | null;
  provider_intent_status?: PaymentStatus | null;
  prepurchase_id?: number | null;
}

/** Respuesta de `POST /prepurchase-sessions/{token}/complete`. */
interface CompletePrepurchaseResponse {
  prepurchaseId: number;
}

/**
 * Pasarela real (ONVO). Habla con dos familias de endpoints vía `ApiService` (el `authInterceptor`
 * añade el Bearer de client_credentials): (a) legacy token-scoped por preorder
 * `/preorders/{token}/payment-intent[/{intentId}]` (createIntent/getStatus); y (b) el flujo DIFERIDO
 * basado en carrito/sesión `POST /checkout/{cartId}/payment-intent`, `GET /checkout/{token}` y
 * `POST /checkout/{token}/complete` (createCheckoutIntent/getCheckoutSession/completeCheckout).
 *
 * La CONFIRMACIÓN del cobro NO pasa por aquí: la dirige el widget web `onvo.pay`
 * en el componente (mount → submitPayment → onSuccess/onError). Por eso `confirm`
 * es un no-op defensivo; el resultado se valida con `getStatus` (+ webhook).
 *
 * La secret key NUNCA llega al front: aquí solo se expone la `publishableKey`
 * (pública) que el componente lee para instanciar el widget.
 *
 * No lleva `@Injectable()`: NUNCA lo instancia Angular por DI (siempre se crea con
 * `new` en la factory de `PAYMENT_PROVIDER`, en contexto de inyección, de ahí que
 * `inject(ApiService)` funcione). Con el decorador, el compilador AOT exigiría un
 * token para `publishableKey: string` (NG2003).
 *
 * Referencia vigente: docs/frontend-agents.md. Los planes P3/P5 originales se conservan como
 * documentación histórica en docs/specs/.
 */
export class OnvoPaymentProvider implements PaymentProvider {
  readonly id = 'onvo' as const;

  private readonly api = inject(ApiService);

  /** Publishable key de ONVO (pública, va al bundle). La lee el componente. */
  constructor(readonly publishableKey: string) {}

  createIntent(preorderToken: string): Observable<PaymentIntentResult> {
    // Error de red: se DEJA propagar para que el componente lo cuente en el cap
    // (C1) y muestre el copy de «no pudimos iniciar el pago».
    return this.api
      .post<CreateIntentResponse>(`/preorders/${encodeURIComponent(preorderToken)}/payment-intent`, {})
      .pipe(
        map((r) => ({
          providerIntentId: r.providerIntentId,
          status: r.status,
          amountColones: r.amountColones,
        })),
      );
  }

  /**
   * No-op defensivo: el camino ONVO confirma vía widget, no por aquí. Devuelve
   * `processing` (no terminal) por si algún consumidor legacy lo invocara.
   */
  confirm(): Observable<PaymentResult> {
    return of<PaymentResult>({ status: 'processing' });
  }

  getStatus(preorderToken: string, intentId: string): Observable<PaymentResult> {
    return this.api
      .get<StatusResponse>(
        `/preorders/${encodeURIComponent(preorderToken)}/payment-intent/${encodeURIComponent(intentId)}`,
      )
      .pipe(
        map(
          (r): PaymentResult => ({
            status: r.status,
            transactionId: r.transactionId,
            errorCode: r.errorCode ?? null,
            message: r.message ?? null,
          }),
        ),
        // Error de red al consultar el estado → sentinel `error`: el componente
        // lo trata como pendiente (el webhook es la vía autoritativa), NO falla.
        catchError(() => of<PaymentResult>({ status: 'error' })),
      );
  }

  createCheckoutIntent(
    cartId: number,
    payload: PreorderRequest,
  ): Observable<CheckoutIntentResult> {
    // El error (422 stock/promo, red) se DEJA propagar: el checkout lo muestra y refresca el carrito.
    return this.api
      .post<CreateCheckoutIntentResponse>(`/checkout/${cartId}/payment-intent`, payload)
      .pipe(
        map((r) => ({
          sessionToken: r.sessionToken,
          providerIntentId: r.providerIntentId,
          status: r.status,
          amountColones: r.amountColones,
          reservationExpiresAt: r.reservationExpiresAt ?? null,
          reservationExpiresInSeconds: r.reservationExpiresInSeconds ?? 0,
        })),
      );
  }

  reserveCheckout(
    cartId: number,
    payload: PreorderRequest,
  ): Observable<CheckoutReserveResult> {
    // El error (409 por `error` sin cupo, 429 throttle, red) se DEJA propagar: el checkout enruta el
    // copy por `error` / status y decide si abrir el modal «avísame» o mostrar el rótulo.
    return this.api
      .post<CheckoutReserveResponse>(`/checkout/${cartId}/reserve`, payload)
      .pipe(
        map((r) => ({
          sessionToken: r.sessionToken,
          reservationExpiresAt: r.reservationExpiresAt ?? null,
          reservationExpiresInSeconds: r.reservationExpiresInSeconds ?? 0,
          amountColones: r.amountColones,
        })),
      );
  }

  getCheckoutSession(sessionToken: string): Observable<CheckoutSessionState> {
    return this.api
      .get<CheckoutSessionResponse>(`/checkout/${encodeURIComponent(sessionToken)}`)
      .pipe(
        map((r) => ({
          sessionToken: r.sessionToken,
          consumed: !!r.consumed,
          preorderToken: r.preorderToken ?? null,
          providerIntentId: r.providerIntentId ?? null,
          ...(r.status ? { status: r.status } : {}),
          amountColones: r.amountColones,
          reservationExpiresAt: r.reservationExpiresAt ?? null,
          reservationExpiresInSeconds: r.reservationExpiresInSeconds ?? 0,
        })),
      );
  }

  completeCheckout(sessionToken: string): Observable<{ token: string }> {
    // El error (409 sin stock, 422 no-confirmado, red) se DEJA propagar: el componente distingue
    // por status HTTP (sin tragarlo) para mostrar el panel correcto.
    return this.api
      .post<CompleteCheckoutResponse>(`/checkout/${encodeURIComponent(sessionToken)}/complete`, {})
      .pipe(map((r) => ({ token: r.token })));
  }

  createPrepurchaseIntent(sessionToken: string): Observable<PrepurchaseIntentResult> {
    // El error (410 reserva expirada, 409 reembolso, red) se DEJA propagar: la página de pago lo
    // resuelve como estado terminal.
    return this.api
      .post<CreatePrepurchaseIntentResponse>(
        `/prepurchase-sessions/${encodeURIComponent(sessionToken)}/payment-intent`,
        {},
      )
      .pipe(
        map((r) => ({
          sessionToken: r.sessionToken,
          providerIntentId: r.providerIntentId,
          status: r.status,
          amountColones: r.amountColones,
        })),
      );
  }

  getPrepurchaseSession(sessionToken: string): Observable<PrepurchaseSessionPaymentState> {
    return this.api
      .get<PrepurchaseSessionResponse>(`/prepurchase-sessions/${encodeURIComponent(sessionToken)}`)
      .pipe(
        map((r) => ({
          sessionToken: r.session_token,
          status: r.status,
          consumed: r.status === 'consumed',
          prepurchaseId: r.prepurchase_id ?? null,
          providerIntentId: r.provider_intent_id ?? null,
          intentStatus: r.provider_intent_status ?? null,
          amountColones: r.amount_colones,
          expiresAt: r.expires_at ?? null,
          expiresInSeconds: r.expires_in_seconds,
        })),
      );
  }

  completePrepurchase(sessionToken: string): Observable<{ prepurchaseId: number }> {
    // El error (409 reembolso, 422 no-confirmado, red) se DEJA propagar: el componente distingue por
    // status HTTP para mostrar el panel correcto.
    return this.api
      .post<CompletePrepurchaseResponse>(
        `/prepurchase-sessions/${encodeURIComponent(sessionToken)}/complete`,
        {},
      )
      .pipe(map((r) => ({ prepurchaseId: r.prepurchaseId })));
  }
}
